import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { needsPrivacyPolicySelector, pinCodeSelector, privacyPolicySelector, userMobileSelector } from 'src/store/selectores/loginSelectors';
import { IRootReducer } from 'src/store/reducers';
import { useSelector, useDispatch } from 'react-redux';
import { getLocalStorageValue, setLocalStorageValue } from 'src/utilis/utilis';
import { StorageKeys } from 'src/types/global';
import { loginByPinCode, privacyPolicyApproval, updateToken, validateSms } from 'src/api/api';
import { unavailableAccountsMessage, updateAccountTokens } from 'src/api/accountTokens';
import { IAccount, ResPinCodeLogin, ResPinCodeSignup, ResPrivacyPolicyApproval, SingupType } from 'src/types/login';
import {
  initConnect,
  initConnectSuccess,
  setErrorMessage,
  setNeedsPrivacyPolicy,
  setPinCode,
  setUserMobile,
} from 'src/store/actions/loginAction';
import { setIsRedirectPopupOpenAction } from 'src/store/slices/common/commonSlice';
import { useHistory } from 'react-router-dom';
import { FcData, ReqStatus } from 'src/api/types';
import { routesEndpoints } from 'src/routes/routes_endpoints';
import { ILoginState } from 'src/store/type';
import {
  Backdrop,
  ModalContainer,
  InnerContainer,
  ContentWrapper,
  ContentContainer,
  TitlesBox,
  TitleText,
  VersionText,
  VersionNumber,
  ContentBox,
  StyledIframe,
  CheckboxContainer,
  CheckboxTextWrapper,
  CheckboxText,
  CheckboxTouchable,
  SubmitButton,
  IframeWrapper,
  IframeOverlay,
  IframeContainer,
} from './privacyPolicyStyles';
import CheckedIcon from '../Icons/CheckedIcon';
import UnCheckedIcon from '../Icons/UnCheckedIcon';
import Alert from '../commons/Alert/Alert';
import { retry } from 'redux-saga/effects';

const getReadOnlyHtml = (content: string) => {
  // Check if content is a full HTML document
  if (content.includes('<html') || content.includes('<!DOCTYPE')) {
    const styles = `
      <style>
        * {
          -webkit-touch-callout: none !important;
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          user-select: none !important;
        }
        a, button, input, select, textarea, [onclick] {
          pointer-events: none !important;
          cursor: default !important;
        }
        header, nav, .header, .nav, .navbar, .menu, #whatsapp-cta {
          display: none !important;
        }
        #contents-header {
          padding-top: 50px;
        }
        html, body {
          border-radius: 12px;
        }
      </style>
    `;

    if (content.includes('</head>')) {
      return content.replace('</head>', `${styles}</head>`);
    }
    return styles + content;
  }

  // If it's just content, wrap it
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <style>
        html, body {
          margin: 0;
          padding: 16px;
          box-sizing: border-box;
          font-family: Arial, sans-serif;
          line-height: 1.6;
        }
        * {
          -webkit-touch-callout: none !important;
          -webkit-user-select: none !important;
          user-select: none !important;
        }
        a, button, input, select, textarea, [onclick] {
          pointer-events: none !important;
          cursor: default !important;
        }
      </style>
    </head>
    <body>
      ${content}
    </body>
    </html>
  `;
};

interface AlertState {
  isShow: boolean;
  text: string;
}

const PrivacyPolicyModal: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [isChecked, setIsChecked] = useState(false);
  const [showAlert, setShowAlert] = useState<AlertState>({
    isShow: false,
    text: t('genralError'),
  });
  const needsPrivacyPolicy = useSelector((state: IRootReducer) => needsPrivacyPolicySelector(state));
  const privacyPolicy = useSelector((state: IRootReducer) => privacyPolicySelector(state));
  const pinCodeState = useSelector((state: IRootReducer) => pinCodeSelector(state));
  const userMobile = useSelector((state: IRootReducer) => userMobileSelector(state));
  const onInitConnect = (payload: { deviceToken: string }) => dispatch(initConnect(payload));
  const onSetLoginData = (payload: ILoginState) => dispatch(initConnectSuccess(payload));

  const [policyHtml, setPolicyHtml] = useState<string>('');
  const [lockedUntil, setLockedUntil] = useState<Date | null>(null);
  const setShowAlertRef = useRef(setShowAlert);
  setShowAlertRef.current = setShowAlert;

  const TOAST_DURATION_S = 6;

  useEffect(() => {
    if (!lockedUntil) return;
    let ticks = 0;
    const interval = setInterval(() => {
      ticks++;
      const remaining = lockedUntil.getTime() - Date.now();
      if (remaining <= 0 || ticks >= TOAST_DURATION_S) {
        clearInterval(interval);
        setLockedUntil(null);
        return;
      }
      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      const timeLeft = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      setShowAlertRef.current({ isShow: true, text: `${t('smsPunishmentLocked')} ${timeLeft} ${t('minutes')}` });
    }, 1000);
    return () => clearInterval(interval);
  }, [lockedUntil, t]);

  useEffect(() => {
    const fetchPolicyContent = async () => {
      if (privacyPolicy.policyUrl && privacyPolicy.policyUrl.length > 0) {
        try {
          const res = await fetch(privacyPolicy.policyUrl);
          const html = await res.text();
          setPolicyHtml(html);
        } catch (error) {
          console.error('Failed to fetch policy:', error);
          setPolicyHtml(privacyPolicy.policyContent || '');
        }
      } else {
        setPolicyHtml(privacyPolicy.policyContent || '');
      }
    };

    if (needsPrivacyPolicy) {
      fetchPolicyContent();
    }
  }, [needsPrivacyPolicy, privacyPolicy.policyUrl, privacyPolicy.policyContent]);

  const retryPinCodeLogin = async (deviceToken: string, pinCode: string) => {
    try {
    const res = await loginByPinCode({
      deviceToken,
      pinCode,
    });

    if (res.response === ResPinCodeLogin.SUCCESS) {
      const { data = [] } = res;

      const loginData: FcData = data[0] || {};
      const fcAccounts: IAccount[] = loginData.fcAccounts || [];
      const token: string = loginData.token || '';
      const indexerR: string = loginData.indexerR || '';
      const { available, unavailable } = await updateAccountTokens(
          fcAccounts,
          ({ proxyUrl, contactUUID, dbUrl, proxyToken }) =>
              updateToken({
                  token,
                  proxyUrl,
                  dbUrl,
                  contactUUID,
                  indexerR,
                  accessToken: proxyToken?.accessToken || '',
                  tokenType: proxyToken?.tokenType || 'Bearer',
              })
      );

      if (fcAccounts.length && !available.length) {
        setShowAlert({ isShow: true, text: t('allCompaniesUnavailable') });
        return;
      }
      if (unavailable.length) dispatch(setErrorMessage(unavailableAccountsMessage(unavailable, t)));

      onSetLoginData({
        loginType: SingupType.PinCode,
        isAuthenticated: ReqStatus.SUCCESS,
        selectedFcAccount: available[0],
        ...data[0],
        fcAccounts: available,
      });

      dispatch(setPinCode(''));
      dispatch(setNeedsPrivacyPolicy(false));
      history.push(`${routesEndpoints.HOME}`);
    } else if (String(res.response) === ResPinCodeLogin.TOO_MANY_ATTEMPTS_LOCKED) {
      const lockedUntilUTC = (res.data as any)?.lockedUntilUTC;
      if (lockedUntilUTC) {
        const lockDate = new Date(lockedUntilUTC);
        const remaining = lockDate.getTime() - Date.now();
        const minutes = Math.floor(remaining / 60000);
        const seconds = Math.floor((remaining % 60000) / 1000);
        const timeLeft = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        setShowAlert({ isShow: true, text: `${t('smsPunishmentLocked')} ${timeLeft} ${t('minutes')}` });
        setLockedUntil(lockDate);
      } else {
        setShowAlert({ isShow: true, text: t('wrongCodeToManyTimes') });
      }
    } else {
      setShowAlert({
        isShow: true,
        text: t('genralError'),
      });
    }
    } catch (error: any) {
      if (error?.response?.status === 401) {
        dispatch(setIsRedirectPopupOpenAction({ isRedirectPopupOpen: true, reason: 'refresh_failed' }));
      } else {
        setShowAlert({ isShow: true, text: t('genralError') });
      }
      console.log('e', error);
    }
  };

  const retryMobileLogin = async (mobile: string, verifyCode: string) => {
    try {
      const deviceToken = getLocalStorageValue(StorageKeys.DeviceToken);
      const res = await validateSms({
        verifyCode,
        deviceToken,
      });

      if (res.data.response === ResPinCodeSignup.SUCCESS) {
        const { data } = res.data;
        setLocalStorageValue(StorageKeys.DeviceToken, res.data.$deviceToken);

        const loginData: ILoginState = data[0] || {};
        const fcAccounts: IAccount[] = loginData.fcAccounts || [];
        const token: string = loginData.token || '';
        const indexerR: string = loginData.indexerR || '';
        const { available, unavailable } = await updateAccountTokens(
            fcAccounts,
            ({ proxyUrl, contactUUID, dbUrl, proxyToken }) =>
                updateToken({
                    token,
                    proxyUrl,
                    dbUrl,
                    contactUUID,
                    indexerR,
                    accessToken: proxyToken?.accessToken || '',
                    tokenType: proxyToken?.tokenType || 'Bearer',
                })
        );

        if (fcAccounts.length && !available.length) {
          setShowAlert({ isShow: true, text: t('allCompaniesUnavailable') });
          return;
        }
        if (unavailable.length) dispatch(setErrorMessage(unavailableAccountsMessage(unavailable, t)));

        onSetLoginData({
          isAuthenticated: ReqStatus.SUCCESS,
          ...data[0],
          fcAccounts: available,
        });
        history.push('signup-options');
        dispatch(setNeedsPrivacyPolicy(false));
        dispatch(setUserMobile(''));
      } else if (res.data.response === ResPinCodeSignup.FAILED_TO_FIND_PHONE) {
        dispatch(setNeedsPrivacyPolicy(false));
        dispatch(setUserMobile(''));
        history.push('/');
      } else if (res.data.response === ResPinCodeSignup.TOO_MANY_ATTEMPTS_LOCKED) {
        const lockedUntilUTC = res.data.data?.lockedUntilUTC;
        if (lockedUntilUTC) {
          const lockDate = new Date(lockedUntilUTC);
          const remaining = lockDate.getTime() - Date.now();
          const minutes = Math.floor(remaining / 60000);
          const seconds = Math.floor((remaining % 60000) / 1000);
          const timeLeft = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
          setShowAlert({ isShow: true, text: `${t('smsPunishmentLocked')} ${timeLeft} ${t('minutes')}` });
          setLockedUntil(lockDate);
        } else {
          setShowAlert({ isShow: true, text: t('wrongCodeToManyTimes') });
        }
      } else {
        setShowAlert({ isShow: true, text: t('genralError') });
      }
    } catch (error) {
      setShowAlert({ isShow: true, text: t('genralError') });
      console.log('e', error);
    }
  }
  const onSubmit = async () => {
    try {
      if (!isChecked) return;
      if (!privacyPolicy.versionNumber) return;

      const deviceToken = getLocalStorageValue(StorageKeys.DeviceToken);
      const res = await privacyPolicyApproval({ deviceToken, versionNumber: privacyPolicy.versionNumber, userMobile });

      const resData = res.data;

      if (resData.response !== '0' && resData.response !== '1') {
        setShowAlert({
          isShow: true,
          text: t('genralError'),
        });
        return;
      }

      if (resData.response === '0' || resData.response === '1') {
        if (pinCodeState.length === 4) {
          await retryPinCodeLogin(deviceToken, pinCodeState);
          return;
        }
        if (userMobile.length > 0 && resData.verifyCode.length > 0) {
          await retryMobileLogin(userMobile, resData.verifyCode);
          return;
        }
        onInitConnect({ deviceToken });
      }
    } catch (error) {
      setShowAlert({
        isShow: true,
        text: t('genralError'),
      });
      console.log('Error privacyPolicy: ', error);
    }
  };

  if (!needsPrivacyPolicy) return null;

  return (
    <>
      <Backdrop>
        <ModalContainer>
          <InnerContainer>
            <ContentWrapper>
              <ContentContainer>
                <TitlesBox>
                  <TitleText>{t('privacyPolicy')}</TitleText>
                  <VersionText>
                    {`${t('versionNum')} - `}
                    <VersionNumber>{privacyPolicy.versionNumber}</VersionNumber>
                  </VersionText>
                </TitlesBox>

                <ContentBox>
                  <IframeContainer>
                    <StyledIframe
                      srcDoc={getReadOnlyHtml(policyHtml)}
                      title="Privacy Policy"
                      sandbox="allow-same-origin"
                    />
                  </IframeContainer>
                </ContentBox>
              </ContentContainer>

              <CheckboxContainer>
                <CheckboxTouchable onClick={() => setIsChecked(prev => !prev)}>
                  {isChecked ? <CheckedIcon /> : <UnCheckedIcon />}
                </CheckboxTouchable>
                <CheckboxTextWrapper>
                  <CheckboxText>
                    {t('policyReadAgree')}
                  </CheckboxText>
                </CheckboxTextWrapper>
              </CheckboxContainer>
            </ContentWrapper>

            <SubmitButton
              onClick={onSubmit}
              disabled={!isChecked}
              $isEnabled={isChecked}
            >
              {t('ok')}
            </SubmitButton>
          </InnerContainer>
        </ModalContainer>
      </Backdrop>
      <Alert
        open={showAlert.isShow}
        onClose={() => setShowAlert(prev => ({ ...prev, isShow: false }))}
        severity="error"
      >
        {showAlert.text}
      </Alert>
    </>
  );
};

export default PrivacyPolicyModal;